import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../../lib/toast';
import { API, auth, errMsg, PrimaryBtn } from '../finance/shared';
import { EmptyState } from '../../../components/dashboard/TodaySchedule';
import Icon from '../../../components/dashboard/DashboardIcons';
import VenueCourtsModal from './VenueCourtsModal';
import VenueWizardModal from './VenueWizardModal';

const BLANK = { name: '', sport_type: '', capacity: '', price_per_hour: '', description: '', city: '', dimensions: '', women_friendly: '', whatsapp_number: '', maps_url: '', amenities: [], operating_hours: [], is_public: true };

export default function VenuesTab({ venues, canManage, onReload }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [courtsVenue, setCourtsVenue] = useState(null);

  const openCreate = () => { setEditing(null); setForm(BLANK); setImageFile(null); setModalOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      name: v.name, sport_type: v.sport_type || '', capacity: v.capacity ?? '', price_per_hour: v.price_per_hour ?? '',
      description: v.description || '', city: v.city || '', dimensions: v.dimensions || '',
      women_friendly: v.women_friendly === true ? 'true' : v.women_friendly === false ? 'false' : '',
      whatsapp_number: v.whatsapp_number || '', maps_url: v.maps_url || '', amenities: v.amenities || [], operating_hours: v.operating_hours || [], is_public: v.is_public !== false,
    });
    setImageFile(null);
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('اسم الملعب مطلوب'); return; }
    setSaving(true);
    const payload = {
      ...form,
      capacity: form.capacity === '' ? null : Number(form.capacity),
      price_per_hour: form.price_per_hour === '' ? null : Number(form.price_per_hour),
      women_friendly: form.women_friendly === '' ? null : form.women_friendly === 'true',
    };
    try {
      let venueId = editing?.id;
      if (editing) await axios.put(`${API}/venues/${editing.id}`, payload, auth());
      else {
        const res = await axios.post(`${API}/venues`, payload, auth());
        venueId = res.data.id;
      }
      if (imageFile) {
        const fd = new FormData();
        fd.append('file', imageFile);
        await axios.post(`${API}/venues/${venueId}/image`, fd, { headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(editing ? 'تم تحديث الملعب' : 'تمت إضافة الملعب بنجاح');
      setModalOpen(false);
      onReload();
    } catch (e) { toast.error(errMsg(e, 'فشل حفظ الملعب')); } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/venues/${confirmDelete.id}`, auth());
      toast.success('تم حذف الملعب');
      setConfirmDelete(null);
      onReload();
    } catch (e) { toast.error(errMsg(e, 'فشل الحذف')); }
  };

  return (
    <div data-testid="venues-tab">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500">إدارة ملاعب منشأتك — الاسم، السعة، السعر لكل ساعة، والصورة</p>
        {canManage && (
          <PrimaryBtn onClick={openCreate} data-testid="venue-add-btn"><Icon name="plus" className="h-4 w-4" /> ملعب جديد</PrimaryBtn>
        )}
      </div>

      {venues === null ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((i) => <div key={i} className="h-40 animate-pulse rounded-xl bg-slate-100" />)}</div>
      ) : venues.length === 0 ? (
        <EmptyState icon="field" text="لا توجد ملاعب بعد — أضف أول ملعب لبدء الجدولة" testId="venues-empty" />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="venues-list">
          {venues.map((v) => (
            <div key={v.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`venue-card-${v.id}`}>
              <div className="flex h-32 items-center justify-center bg-slate-100">
                {v.image_storage_path ? (
                  <img src={`${API}/venues/${v.id}/image`} alt={v.name} className="h-full w-full object-cover" />
                ) : (
                  <Icon name="field" className="h-10 w-10 text-slate-300" />
                )}
              </div>
              <div className="p-4">
                <h4 className="font-bold text-slate-900" data-testid={`venue-name-${v.id}`}>{v.name}</h4>
                <p className="mt-1 text-xs text-slate-500">{v.sport_type || '—'} {v.capacity ? `• سعة ${v.capacity}` : ''} {v.dimensions ? `• ${v.dimensions}` : ''}</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {v.city && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{v.city}</span>}
                  {v.women_friendly === true && <span className="rounded-full bg-pink-50 px-2 py-0.5 text-[11px] font-semibold text-pink-600">مناسب للسيدات</span>}
                  {v.is_public === false && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-600">غير منشور بالسوق العام</span>}
                </div>
                <p className="mt-1.5 text-sm font-semibold text-teal-700">{v.price_per_hour ? `${v.price_per_hour} ر.س / ساعة` : 'السعر غير محدد'}</p>
                {canManage && (
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => openEdit(v)} data-testid={`venue-edit-${v.id}`} className="flex-1 rounded-lg border border-slate-200 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50">تعديل</button>
                    <button onClick={() => setCourtsVenue(v)} data-testid={`venue-courts-btn-${v.id}`} className="flex-1 rounded-lg border border-teal-200 py-1.5 text-xs font-bold text-teal-700 hover:bg-teal-50">المقاسات</button>
                    <button onClick={() => setConfirmDelete(v)} data-testid={`venue-delete-${v.id}`} className="flex-1 rounded-lg border border-red-200 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50">حذف</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <VenueWizardModal
        open={modalOpen} editing={editing} form={form} setForm={setForm}
        imageFile={imageFile} setImageFile={setImageFile} saving={saving}
        onSave={save} onClose={() => setModalOpen(false)}
      />

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="venue-delete-confirm-modal">
          <div className="w-full max-w-sm rounded-xl bg-white p-6">
            <h4 className="font-bold text-slate-900">تأكيد الحذف</h4>
            <p className="mt-2 text-sm text-slate-500">سيتم حذف ملعب «{confirmDelete.name}» وكل حجوزاته نهائياً. هل أنت متأكد؟</p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600">إلغاء</button>
              <button onClick={remove} data-testid="venue-delete-confirm-btn" className="flex-1 rounded-lg bg-red-600 py-2.5 text-sm font-bold text-white">تأكيد الحذف</button>
            </div>
          </div>
        </div>
      )}

      {courtsVenue && <VenueCourtsModal venue={courtsVenue} onClose={() => setCourtsVenue(null)} />}
    </div>
  );
}
